/**
 * MA-KNOWLEDGE-INJECTION-004: Knowledge Staleness Indicator
 *
 * Shows a warning when Business Knowledge has been updated since the last
 * generation. Suggests regeneration but does not block.
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export function KnowledgeStalenessIndicator({ profitPathId, storedHash, stepLabel, onRegenerate }) {
  const { data: knowledge } = useQuery({
    queryKey: ["knowledge", profitPathId],
    queryFn: () =>
      api.get(`/profit-paths/${profitPathId}/knowledge`).then((r) => r.data),
    enabled: !!profitPathId && !!storedHash,
    staleTime: 30_000,
  });

  if (!storedHash || !knowledge?.knowledge_hash) return null;
  if (knowledge.knowledge_hash === storedHash) return null;

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/[0.06] border border-amber-500/15"
      data-testid={`knowledge-staleness-${stepLabel}`}
    >
      <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
      <p className="text-[11px] text-amber-300/80 flex-1">
        Business Knowledge has been updated since {stepLabel} was generated.
        Regenerating may improve output quality.
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-1 h-5 px-2 rounded text-[10px] font-medium bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors shrink-0"
          data-testid={`knowledge-regen-${stepLabel}`}
        >
          <RefreshCw className="h-2.5 w-2.5" />
          Regenerate
        </button>
      )}
    </div>
  );
}

/**
 * Knowledge Strength Warning
 * Shows a soft message when generation returns a knowledge_warning.
 */
export function KnowledgeStrengthWarning({ warning }) {
  if (!warning) return null;
  return (
    <div
      className="flex items-start gap-2 px-3 py-2 rounded-md bg-slate-800/40 border border-slate-700/30"
      data-testid="knowledge-strength-warning"
    >
      <AlertTriangle className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
      <p className="text-[11px] text-slate-400">{warning}</p>
    </div>
  );
}
